/**
 * Knowledge Article Modal Component
 * Modal for creating and editing knowledge base articles
 */

import React, { useState, useEffect } from 'react';
import { useTheme } from '@/contexts/ThemeContext';
import type { KnowledgeArticle } from '@/lib/types/call-center-settings';

interface KnowledgeArticleModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: (data: Partial<KnowledgeArticle>) => Promise<void>;
  article?: KnowledgeArticle | null;
  companyId: string;
}

export function KnowledgeArticleModal({ isOpen, onClose, onSave, article, companyId }: KnowledgeArticleModalProps) {
  const { theme } = useTheme();
  const [formData, setFormData] = useState({
    title: '',
    category: 'general',
    content: '',
    tags: [] as string[],
    is_published: true
  });
  const [tagInput, setTagInput] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (article) {
      setFormData({
        title: article.title,
        category: article.category,
        content: article.content,
        tags: article.tags || [],
        is_published: article.is_published
      });
    } else {
      setFormData({
        title: '',
        category: 'general',
        content: '',
        tags: [],
        is_published: true
      });
    }
    setTagInput('');
  }, [article, isOpen]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSaving(true);
    try {
      await onSave({
        ...formData,
        company_id: companyId
      });
      onClose();
    } catch (error) {
      console.error('Error saving article:', error);
      alert('Failed to save article');
    } finally {
      setSaving(false);
    }
  };

  const handleAddTag = () => {
    const newTag = tagInput.trim().toLowerCase();
    if (newTag && !formData.tags.includes(newTag)) {
      setFormData({ ...formData, tags: [...formData.tags, newTag] });
      setTagInput('');
    }
  };

  const handleRemoveTag = (tagToRemove: string) => {
    setFormData({ ...formData, tags: formData.tags.filter(tag => tag !== tagToRemove) });
  };

  const handleTagInputKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleAddTag();
    }
  };

  if (!isOpen) return null;

  return (
    <div 
      className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50"
      onClick={onClose}
    >
      <div 
        className="rounded-lg p-6 max-w-3xl w-full mx-4 max-h-[90vh] overflow-y-auto"
        style={{ backgroundColor: theme.colors.bgCard }}
        onClick={(e) => e.stopPropagation()}
      >
        <h2 className="text-2xl font-bold mb-4" style={{ color: theme.colors.textPrimary }}>
          {article ? 'Edit Knowledge Article' : 'New Knowledge Article'}
        </h2>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-sm font-medium mb-2" style={{ color: theme.colors.textSecondary }}>
              Title *
            </label>
            <input
              type="text"
              required
              value={formData.title}
              onChange={(e) => setFormData({ ...formData, title: e.target.value })}
              className="w-full px-3 py-2 rounded border"
              style={{
                backgroundColor: theme.colors.bgPage,
                borderColor: theme.colors.borderColor,
                color: theme.colors.textPrimary
              }}
              placeholder="e.g., HVAC System Coverage Details"
            />
          </div>

          <div>
            <label className="block text-sm font-medium mb-2" style={{ color: theme.colors.textSecondary }}>
              Category *
            </label>
            <select
              required
              value={formData.category}
              onChange={(e) => setFormData({ ...formData, category: e.target.value })}
              className="w-full px-3 py-2 rounded border"
              style={{
                backgroundColor: theme.colors.bgPage,
                borderColor: theme.colors.borderColor,
                color: theme.colors.textPrimary
              }}
            >
              <option value="general">General</option>
              <option value="coverage">Coverage</option>
              <option value="claims">Claims</option>
              <option value="pricing">Pricing</option>
              <option value="procedures">Procedures</option>
              <option value="faq">FAQ</option>
              <option value="troubleshooting">Troubleshooting</option>
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium mb-2" style={{ color: theme.colors.textSecondary }}>
              Content *
            </label>
            <textarea
              required
              value={formData.content}
              onChange={(e) => setFormData({ ...formData, content: e.target.value })}
              rows={10}
              className="w-full px-3 py-2 rounded border"
              style={{
                backgroundColor: theme.colors.bgPage,
                borderColor: theme.colors.borderColor,
                color: theme.colors.textPrimary
              }}
              placeholder="Enter the article content..."
            />
            <p className="text-xs mt-1" style={{ color: theme.colors.textSecondary }}>
              Supports markdown formatting
            </p>
          </div>

          <div>
            <label className="block text-sm font-medium mb-2" style={{ color: theme.colors.textSecondary }}>
              Tags
            </label>
            <div className="flex gap-2 mb-2">
              <input
                type="text"
                value={tagInput}
                onChange={(e) => setTagInput(e.target.value)}
                onKeyDown={handleTagInputKeyDown}
                className="flex-1 px-3 py-2 rounded border"
                style={{
                  backgroundColor: theme.colors.bgPage,
                  borderColor: theme.colors.borderColor,
                  color: theme.colors.textPrimary
                }}
                placeholder="Add tags..."
              />
              <button
                type="button"
                onClick={handleAddTag}
                className="px-4 py-2 rounded font-medium"
                style={{
                  backgroundColor: theme.colors.bgHover,
                  color: theme.colors.textPrimary,
                  border: `1px solid ${theme.colors.borderColor}`
                }}
              >
                Add Tag
              </button>
            </div>
            {formData.tags.length > 0 && (
              <div className="flex flex-wrap gap-2">
                {formData.tags.map((tag) => (
                  <span
                    key={tag}
                    className="inline-flex items-center px-3 py-1 rounded-full text-sm"
                    style={{
                      backgroundColor: theme.colors.infoBg,
                      color: theme.colors.infoText,
                      border: `1px solid ${theme.colors.infoBorder}`
                    }}
                  >
                    {tag}
                    <button
                      type="button"
                      onClick={() => handleRemoveTag(tag)}
                      className="ml-2 hover:opacity-70"
                    >
                      ×
                    </button>
                  </span>
                ))}
              </div>
            )}
          </div>

          <div className="flex items-center">
            <input
              type="checkbox"
              id="is_published"
              checked={formData.is_published}
              onChange={(e) => setFormData({ ...formData, is_published: e.target.checked })}
              className="mr-2"
            />
            <label htmlFor="is_published" className="text-sm" style={{ color: theme.colors.textPrimary }}>
              Published (visible to agents)
            </label>
          </div>

          <div className="flex justify-end space-x-3 pt-4">
            <button
              type="button"
              onClick={onClose}
              disabled={saving}
              className="px-4 py-2 rounded font-medium"
              style={{
                backgroundColor: theme.colors.bgHover,
                color: theme.colors.textPrimary,
                border: `1px solid ${theme.colors.borderColor}`
              }}
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={saving}
              className="px-4 py-2 rounded font-medium"
              style={{
                backgroundColor: theme.colors.primaryColor,
                color: '#ffffff'
              }}
            >
              {saving ? 'Saving...' : (article ? 'Update Article' : 'Create Article')}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
